import { createStore, select, withProps } from '@ngneat/elf';
import { withRequestsCache, withRequestsStatus } from '@ngneat/elf-requests';
import { Injectable } from "@angular/core";
import { RestService } from "../../../shared/services/rest.service";
import { tap, catchError, map, concatMap, finalize, shareReplay, filter, take } from "rxjs/operators";
import { AnimalWhereaboutModel } from '../models/animal-whereabout.model';
import { Observable, of } from 'rxjs';
import { AnimalRepository } from './animal.repository';
import { AnimalDocumentsRepository } from './animal-documents.repository';
import { AnimalWhereaboutsType } from '../models/animal-wherabout-types.enum';


export interface AnimalWhereaboutsState {
  whereabouts: {
    [animalId: number]: AnimalWhereaboutModel[] | null;
  };
  pendingReports: AnimalWhereaboutModel[] | null;
}

export const animalWhereaboutsStore = createStore(
  { name: 'animalWhereabouts' },
  withProps<AnimalWhereaboutsState>({ whereabouts: {}, pendingReports: null }),
  withRequestsCache<'animalWhereabouts'>(),
  withRequestsStatus<'animalWhereabouts'>()
);

@Injectable({ providedIn: 'root' })
export class AnimalWhereaboutsRepository {
  private readonly inFlight = new Map<number, Observable<AnimalWhereaboutModel[]>>();

  constructor(
    private readonly _restService: RestService,
    private readonly _animalRepository: AnimalRepository,
    private readonly _documentsRepository: AnimalDocumentsRepository
  ) {
    // silence is golden
  }

  getByAnimalId(animalId: number, cached: boolean = true): Observable<AnimalWhereaboutModel[]> {
    if (!animalId) {
      return of([]);
    }

    if (!cached) {
      return this._restService.httpGet(`/animal_whereabouts?animal.id=${animalId}`).pipe(
        tap(response => {
          if (!response || !Array.isArray(response)) {
            return;
          }
          animalWhereaboutsStore.update(state => ({
            ...state,
            whereabouts: {
              ...state.whereabouts,
              [animalId]: response
            }
          }));
        }),
        catchError(() => of([]))
      );
    }

    // synchroner Cache-Check: liegt bereits etwas im Store, kein HTTP
    const cachedWhereabouts = animalWhereaboutsStore.getValue().whereabouts[animalId] ?? null;
    if (cachedWhereabouts !== null) {
      return of(cachedWhereabouts);
    }

    // läuft bereits ein Request für dieses Tier, dessen geteiltes Observable zurückgeben
    const pending = this.inFlight.get(animalId);
    if (pending) {
      return pending;
    }

    const request$ = this._restService.httpGet(`/animal_whereabouts?animal.id=${animalId}`).pipe(
      tap(response => {
        if (!response || !Array.isArray(response)) {
          return;
        }
        animalWhereaboutsStore.update(state => ({
          ...state,
          whereabouts: {
            ...state.whereabouts,
            [animalId]: response
          }
        }));
      }),
      map(response => (Array.isArray(response) ? response : [])),
      catchError(() => of([] as AnimalWhereaboutModel[])),
      finalize(() => this.inFlight.delete(animalId)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlight.set(animalId, request$);
    return request$;
  }

  getAll() {
    return this._restService.httpGet('/animal_whereabouts?pagination=false')
      .pipe(
        tap((whereabouts: AnimalWhereaboutModel[]) => {
          if (whereabouts && whereabouts.length > 0) {
            animalWhereaboutsStore.destroy();
            this.setAll(whereabouts);
          }
        })
      );
  }

  loadAllIfEmpty(): Observable<void> {
    const state = animalWhereaboutsStore.getValue();
    const isEmpty = Object.keys(state.whereabouts).length === 0;

    if (!isEmpty) {
      return of(undefined);
    }

    return this.getAll().pipe(map(() => void 0));
  }

  setAll(animalWhereabouts: AnimalWhereaboutModel[]) {
    const updatedWhereabouts = animalWhereabouts.reduce((acc, entry) => {
      const animalId = entry.animal?.id;
      if (animalId) {
        acc[animalId] = [...(acc[animalId] || []), entry];
      }
      return acc;
    }, {} as Record<number, AnimalWhereaboutModel[]>);

    animalWhereaboutsStore.update(state => ({
      ...state,
      whereabouts: {
        ...state.whereabouts,
        ...updatedWhereabouts
      }
    }));
  }


  getPendingReports(): Observable<AnimalWhereaboutModel[]> {
    return this._restService.httpGet('/animal_whereabouts?pendingReport=true&pendingReportOver24h=true&short=true').pipe(
      catchError(() => of([])),
      tap((pendingReports: AnimalWhereaboutModel[]) => {
        animalWhereaboutsStore.update(state => ({ ...state, pendingReports }));
      })
    );
  }

  selectPendingReports(): Observable<AnimalWhereaboutModel[]> {
    return animalWhereaboutsStore.pipe(
      select(state => state.pendingReports),
      filter((pendingReports): pendingReports is AnimalWhereaboutModel[] => !!pendingReports)
    );
  }

  // Lädt die Dashboard-Liste "PDF erstellt & noch nicht versendet" neu, z.B. nach Versand eines Abholungsberichts
  refreshPendingReports(): void {
    this.getPendingReports().pipe(take(1)).subscribe();
  }

  create(payload: any) {
    return this._restService.httpPost('/animal_whereabouts', payload).pipe(
      tap((response: AnimalWhereaboutModel) => {
        const animalId = response.animal?.id;
        if (!animalId) return;

        animalWhereaboutsStore.update(state => ({
          ...state,
          whereabouts: {
            ...state.whereabouts,
            [animalId]: [...(state.whereabouts[animalId] || []), response],
          },
        }));
        // Backend erzeugt beim Anlegen eine Abholungs-PDF als Dokument
        if (response.type === AnimalWhereaboutsType.FOUNDANIMAL_RETURN) {
          this._documentsRepository.refreshDocuments(animalId);
        }
      }),

      concatMap((response: AnimalWhereaboutModel) => {
        const animalId = response.animal?.id;
        if (!animalId) return of(response);

        return this._animalRepository.getById(animalId, false).pipe(
          map(() => response),
          catchError(() => of(response))
        );
      })
    );
  }

  update(id: number, payload: any) {
    return this._restService.httpPut(`/animal_whereabouts/${id}`, payload).pipe(
      tap((response: AnimalWhereaboutModel) => {
        const animalId = response.animal?.id;
        if (!animalId) return;

        animalWhereaboutsStore.update(state => {
          const currentList = state.whereabouts?.[animalId] ?? [];

          const updatedList = currentList.map(entry =>
            entry.id === response.id ? { ...response } : { ...entry }
          );

          return {
            ...state,
            whereabouts: {
              ...state.whereabouts,
              [animalId]: updatedList
            }
          };
        });
      })
    );
  }

  clearByAnimalIdAndStateId(animalId: number, stateId: number) {
    animalWhereaboutsStore.update(state => {
      const current = state.whereabouts[animalId];

      if (!current || !Array.isArray(current)) {
        return state; // nichts zu tun
      }

      return {
        ...state,
        whereabouts: {
          ...state.whereabouts,
          [animalId]: current.filter(entry => entry.id !== stateId)
        }
      };
    });
  }

  delete(id: number, animalId: number) {
    return this._restService.httpDelete(`/animal_whereabouts/${id}`).pipe(
      tap(() => {
        animalWhereaboutsStore.update(state => ({
          ...state,
          whereabouts: {
            ...state.whereabouts,
            [animalId]: null
          }
        }));
      })
    );
  }
}
